import React, { useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useListMaintenanceRequestsQuery } from "../../../store/api/maintenanceApi";

interface BarDataItem {
	month: string;
	completed: number;
	pending: number;
	inProgress: number;
}

interface PieDataItem {
	name: string;
	value: number;
	color: string;
}

interface TooltipPayload {
	dataKey: string;
	value: number;
	color: string;
}

const MaintenanceChart: React.FC = () => {
	const [dateRange, setDateRange] = useState("year");

	// Fetch all maintenance requests for chart data
	const { data: maintenanceData, isLoading } = useListMaintenanceRequestsQuery({
		page: 1,
		page_size: 1000, // Get all for chart
	});

	// Calculate pie chart data from current status
	const pieData: PieDataItem[] = useMemo(() => {
		try {
			if (!maintenanceData?.results) {
				return [
					{ name: "Completed", value: 0, color: "#10B981" },
					{ name: "In Progress", value: 0, color: "#F59E0B" },
					{ name: "Pending", value: 0, color: "#EF4444" },
				];
			}

			const completed = maintenanceData.results.filter(
				(r) => r?.status === "completed",
			).length;
			const inProgress = maintenanceData.results.filter(
				(r) => r?.status === "in_progress",
			).length;
			const pending = maintenanceData.results.filter(
				(r) => r?.status === "pending",
			).length;

			return [
				{ name: "Completed", value: completed, color: "#10B981" },
				{ name: "In Progress", value: inProgress, color: "#F59E0B" },
				{ name: "Pending", value: pending, color: "#EF4444" },
			];
		} catch (error) {
			console.error("Error calculating pie chart data:", error);
			return [
				{ name: "Completed", value: 0, color: "#10B981" },
				{ name: "In Progress", value: 0, color: "#F59E0B" },
				{ name: "Pending", value: 0, color: "#EF4444" },
			];
		}
	}, [maintenanceData]);

	// Calculate bar chart data by month based on selected range
	const barData: BarDataItem[] = useMemo(() => {
		const months = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];

		if (!maintenanceData?.results) {
			// Return empty data for the selected range
			let monthsToShow = 7;
			if (dateRange === "12months") {
				monthsToShow = 12;
			} else if (dateRange === "year") {
				monthsToShow = 12;
			}
			return months.slice(0, monthsToShow).map((month) => ({
				month,
				completed: 0,
				pending: 0,
				inProgress: 0,
			}));
		}

		const now = new Date();
		const currentMonth = now.getMonth();
		const currentYear = now.getFullYear();

		// Determine which months to show based on dateRange
		let monthsToShow: number;
		let startMonth: number;
		let startYear: number;

		if (dateRange === "year") {
			// Show all 12 months of the current year
			monthsToShow = 12;
			startMonth = 0;
			startYear = currentYear;
		} else if (dateRange === "12months") {
			// Show last 12 months (may span across years)
			monthsToShow = 12;
			startMonth = currentMonth - 11; // Go back 11 months (current month + 11 previous)
			startYear = currentYear;
			// Adjust if we go into previous year
			while (startMonth < 0) {
				startMonth += 12;
				startYear -= 1;
			}
		} else {
			// "7months" - Show last 7 months
			monthsToShow = 7;
			startMonth = currentMonth - 6; // Go back 6 months (current month + 6 previous)
			startYear = currentYear;
			// Adjust if we go into previous year
			while (startMonth < 0) {
				startMonth += 12;
				startYear -= 1;
			}
		}

		// Generate month data for the selected range
		const monthData: BarDataItem[] = [];
		for (let i = 0; i < monthsToShow; i++) {
			const monthIndex = (startMonth + i) % 12;
			const year = startYear + Math.floor((startMonth + i) / 12);
			const monthName = months[monthIndex];

			// Filter maintenance requests for this specific month and year
			const monthRequests = maintenanceData.results.filter((r) => {
				const requestDate = new Date(r.created_at);
				return (
					requestDate.getMonth() === monthIndex &&
					requestDate.getFullYear() === year
				);
			});

			monthData.push({
				month: monthName,
				completed: monthRequests.filter((r) => r.status === "completed").length,
				pending: monthRequests.filter((r) => r.status === "pending").length,
				inProgress: monthRequests.filter((r) => r.status === "in_progress")
					.length,
			});
		}

		return monthData;
	}, [maintenanceData, dateRange]);

	const CustomTooltip: React.FC<{
		active?: boolean;
		payload?: TooltipPayload[];
		label?: string;
	}> = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const formatLabel = (dataKey: string): string => {
				const labels: Record<string, string> = {
					completed: "Completed",
					pending: "Pending",
					inProgress: "In Progress",
				};
				return labels[dataKey] || dataKey;
			};

			return (
				<div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
					<p className="font-medium text-popover-foreground mb-2">{label}</p>
					{payload.map((entry: TooltipPayload, index: number) => (
						<p key={index} className="text-sm" style={{ color: entry.color }}>
							{formatLabel(entry.dataKey)}: {entry.value}
						</p>
					))}
				</div>
			);
		}
		return null;
	};

	return (
		<div className="bg-card border border-border rounded-lg p-6 card-shadow">
			<div className="flex items-center justify-between mb-6">
				<h3 className="text-lg font-semibold text-foreground">
					Maintenance Overview
				</h3>
				<select
					value={dateRange}
					onChange={(e) => setDateRange(e.target.value)}
					className="text-sm border border-border rounded-md px-3 py-1 bg-input text-foreground"
				>
					<option value="7months">Last 7 months</option>
					<option value="12months">Last 12 months</option>
					<option value="year">This year</option>
				</select>
			</div>

			{isLoading ? (
				<div className="text-center py-12 text-muted-foreground">
					Loading maintenance data...
				</div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* Bar Chart */}
					<div>
						<h4 className="text-sm font-medium text-foreground mb-4">
							Monthly Trends
						</h4>
						<div className="h-64">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={barData}>
									<CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
									<XAxis
										dataKey="month"
										tick={{ fontSize: 12, fill: "#6B7280" }}
										axisLine={{ stroke: "#E5E7EB" }}
									/>
									<YAxis
										tick={{ fontSize: 12, fill: "#6B7280" }}
										axisLine={{ stroke: "#E5E7EB" }}
									/>
									<Tooltip content={<CustomTooltip />} />
									<Bar
										dataKey="completed"
										fill="#10B981"
										radius={[2, 2, 0, 0]}
									/>
									<Bar
										dataKey="inProgress"
										fill="#F59E0B"
										radius={[2, 2, 0, 0]}
									/>
									<Bar dataKey="pending" fill="#EF4444" radius={[2, 2, 0, 0]} />
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>

					{/* Pie Chart */}
					<div>
						<h4 className="text-sm font-medium text-foreground mb-4">
							Current Status
						</h4>
						<div className="h-64 flex items-center">
							<div className="w-1/2">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={pieData}
											cx="50%"
											cy="50%"
											innerRadius={30}
											outerRadius={70}
											paddingAngle={2}
											dataKey="value"
										>
											{pieData.map((entry, index) => (
												<Cell key={`cell-${index}`} fill={entry.color} />
											))}
										</Pie>
										<Tooltip content={<CustomTooltip />} />
									</PieChart>
								</ResponsiveContainer>
							</div>
							<div className="w-1/2 space-y-3">
								{pieData.map((item, index) => (
									<div key={index} className="flex items-center space-x-2">
										<div
											className="w-3 h-3 rounded-full"
											style={{ backgroundColor: item.color }}
										></div>
										<span className="text-sm text-foreground">{item.name}</span>
										<span className="text-sm font-medium text-foreground ml-auto">
											{item.value}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const MaintenanceChartComponent = MaintenanceChart;
export default MaintenanceChartComponent;
